import React, { useMemo } from 'react';
import { Button, Card, Icon, Input, Textarea } from '@repo/ui';
import { numberUtil } from '@xtnd/web/shared';
import { AppealSummary } from '../../appeals/AppealSummary';

interface WriteOffRemainingFormProps {
  matterDisplayNumber: string;
  responsibleAttorneyName: string;
  clientName: string;
  invoiceDate: string;
  invoiceNumber: string;
  outstandingAmount: number;
  amountPaid: number;
  totalAmount: number;
  creditsIssued: number;
  outstandingBalance: number;
  feesCut: string;
  expensesCut: string;
  comment: string;
  errors: Partial<Record<'feesCut' | 'expensesCut', string>>;
  isLoading: boolean;
  onFeesCutChange: (value: string) => void;
  onExpensesCutChange: (value: string) => void;
  onCommentChange: (value: string) => void;
  onSubmit: () => void;
}

export const WriteOffRemainingForm: React.FC<WriteOffRemainingFormProps> = ({
  invoiceNumber,
  outstandingAmount,
  amountPaid,
  matterDisplayNumber,
  responsibleAttorneyName,
  clientName,
  invoiceDate,
  totalAmount,
  creditsIssued,
  outstandingBalance,
  feesCut,
  expensesCut,
  comment,
  errors,
  isLoading,
  onFeesCutChange,
  onExpensesCutChange,
  onCommentChange,
  onSubmit,
}) => {
  const amountToWriteOff = useMemo(() => {
    const fees = parseFloat(feesCut) || 0;
    const expenses = parseFloat(expensesCut) || 0;
    return Math.round((fees + expenses) * 100) / 100;
  }, [feesCut, expensesCut]);

  const sumMismatch = useMemo(() => {
    const expected = Math.round(outstandingBalance * 100) / 100;
    return amountToWriteOff !== expected;
  }, [outstandingBalance, amountToWriteOff]);

  const isFormValid = useMemo(() => {
    const hasError = Object.values(errors ?? {}).some(error => Boolean(error));
    if (hasError) return false;
    if (amountToWriteOff <= 0) return false;
    if (sumMismatch) return false;
    return true;
  }, [errors, amountToWriteOff, sumMismatch]);

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Side: Form */}
          <div className="flex-1 space-y-6">
            {/* Write-Off Details Card */}
            <Card className="p-6 opacity-0 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-xtnd-light dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
                    <Icon
                      name="HiCurrencyDollar"
                      className="w-5 h-5 text-xtnd-blue dark:text-xtnd-blue-300"
                    />
                  </div>
                  <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                    Write-Off Details
                  </h2>
                </div>
              </div>

              <div className="space-y-4">
                {/* Outstanding Balance (read-only) */}
                <div>
                  <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                    Outstanding Balance
                  </p>
                  <p className="text-lg font-mono font-bold text-xtnd-dark dark:text-xtnd-light">
                    {numberUtil.formatCurrency(outstandingBalance)}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Fees Cut */}
                  <div>
                    <label className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                      Fees Cut <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="fees-cut"
                      type="number"
                      value={feesCut}
                      onChange={e => onFeesCutChange(e.target.value)}
                      error={errors.feesCut}
                      disabled={isLoading}
                      required
                      fullWidth
                      placeholder="0.00"
                      min={0}
                      step={0.01}
                      className="dark:border-xtnd-dark-600"
                    />
                  </div>

                  {/* Expenses Cut */}
                  <div>
                    <label className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                      Expenses Cut <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="expenses-cut"
                      type="number"
                      value={expensesCut}
                      onChange={e => onExpensesCutChange(e.target.value)}
                      error={errors.expensesCut}
                      disabled={isLoading}
                      required
                      fullWidth
                      placeholder="0.00"
                      min={0}
                      step={0.01}
                      className="dark:border-xtnd-dark-600"
                    />
                  </div>
                </div>

                {/* Amount to Write Off (calculated) */}
                <div>
                  <p className="block text-xs font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
                    Amount to Write Off
                  </p>
                  <p
                    className={`text-lg font-mono font-bold ${
                      sumMismatch
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-xtnd-dark dark:text-xtnd-light'
                    }`}
                  >
                    {numberUtil.formatCurrency(amountToWriteOff)}
                  </p>
                  {sumMismatch && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                      Fees Cut + Expenses Cut must equal the Outstanding Balance (
                      {numberUtil.formatCurrency(outstandingBalance)}).{' '}
                      {amountToWriteOff < outstandingBalance
                        ? `Missing: ${numberUtil.formatCurrency(Math.round((outstandingBalance - amountToWriteOff) * 100) / 100)}`
                        : `Excess: ${numberUtil.formatCurrency(Math.round((amountToWriteOff - outstandingBalance) * 100) / 100)}`}
                    </p>
                  )}
                </div>
              </div>
            </Card>

            {/* Comment Section */}
            <Card className="p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gray-100 dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
                  <Icon
                    name="HiDocumentText"
                    className="w-5 h-5 text-xtnd-dark-600 dark:text-xtnd-light-400"
                  />
                </div>
                <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">Comment</h2>
              </div>

              <Textarea
                value={comment}
                onChange={e => onCommentChange(e.target.value)}
                disabled={isLoading}
                rows={4}
                fullWidth
                placeholder="Add a comment for this write-off..."
              />
            </Card>
          </div>

          {/* Right Side: Invoice Summary Sidebar */}
          <div className="lg:w-80 space-y-6">
            <AppealSummary
              basicInformation={{
                matterDisplayNumber,
                clientName,
                invoiceDate,
                invoiceNumber,
                totalAmount,
                amountDue: outstandingAmount,
                paid: amountPaid,
                creditsIssued,
                responsibleAttorneyName,
              }}
            />

            <Card
              className="!bg-transparent text-xtnd-dark dark:text-xtnd-light p-6 rounded-2xl shadow-xl sticky top-24 opacity-0 animate-fadeInRight"
              style={{ animationDelay: '0.15s' }}
            >
              <div className="space-y-4">
                <div className="p-3 bg-xtnd-blue/50 rounded-xl border border-xtnd-blue/80 shadow-sm">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[0.625rem] shadow-sm text-xtnd-dark dark:text-xtnd-light uppercase font-bold">
                      Amount to Write Off
                    </span>
                    <span className="text-xtnd-dark dark:text-xtnd-light font-mono font-bold text-lg">
                      {numberUtil.formatCurrency(amountToWriteOff)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                variant="primary"
                onClick={onSubmit}
                disabled={!isFormValid || isLoading}
                loading={isLoading}
                fullWidth
                className="mt-6"
              >
                Confirm
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default WriteOffRemainingForm;
